import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { brandsService } from "api/services/brands.service";
import ContentHeader from "components/Layout/ContentHeader";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import { Input, Select } from "components/UI/Input";
import Modal from "components/UI/Modal";
import Table, { Column } from "components/UI/Table";
import { extractList, getApiErrorMessage } from "utils/api-error";
import { BRAND_DESCRIPTION_MAX_LENGTH, BRAND_FORM_INITIAL, BRAND_NAME_MAX_LENGTH, BRAND_STATUS_FILTER_OPTIONS, BRAND_STATUS_OPTIONS, normalizeBrandName } from "utils/constant/brand";

import type { AdminBrand, AdminBrandCreateRequest, AdminBrandListParams, AdminBrandUpdateRequest } from "api/types/brand.types";
import "pages/AdminPage.scss";
import "./BrandList.scss";

const BrandList: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<AdminBrand[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<AdminBrand | null>(null);
  const [form, setForm] = useState<AdminBrandCreateRequest>(BRAND_FORM_INITIAL);

  const [deactivating, setDeactivating] = useState<AdminBrand | null>(null);
  const [deactivateLoading, setDeactivateLoading] = useState(false);

  const fetchList = useCallback(async (params: AdminBrandListParams) => {
    setLoading(true);
    setError("");
    try {
      const res = await brandsService.adminList(params);
      setItems(extractList<AdminBrand>(res.data));
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  // search·is_active 는 서버 필터다. 빈 문자열 is_active 는 400 이므로 키 자체를 뺀다.
  const buildParams = useCallback((): AdminBrandListParams => {
    const params: AdminBrandListParams = {};
    if (search.trim()) params.search = search.trim();
    if (statusFilter === "true" || statusFilter === "false") params.is_active = statusFilter;
    return params;
  }, [search, statusFilter]);

  useEffect(() => {
    const timer = setTimeout(() => fetchList(buildParams()), 300);
    return () => clearTimeout(timer);
  }, [buildParams, fetchList]);

  /* ───────── 모달 열기/닫기 ───────── */
  const openCreateModal = () => {
    setEditing(null);
    setForm(BRAND_FORM_INITIAL);
    setShowModal(true);
  };

  const openEditModal = (item: AdminBrand) => {
    setEditing(item);
    setForm({
      name: item.name,
      description: item.description ?? "",
      is_active: item.is_active,
    });
    setShowModal(true);
  };

  /* ───────── 추가 / 수정 ───────── */
  const normalizedName = normalizeBrandName(form.name);
  const nameInvalid = !normalizedName || normalizedName.length > BRAND_NAME_MAX_LENGTH;

  const handleSave = async () => {
    if (nameInvalid) return;
    setSaving(true);
    try {
      if (editing) {
        // 기본 브랜드는 name 변경·비활성화가 403 이므로 description 만 보낸다.
        const payload: AdminBrandUpdateRequest = editing.is_default ? { description: form.description } : { ...form, name: normalizedName };
        await brandsService.adminUpdate(editing.id, payload);
      } else {
        await brandsService.adminCreate({ ...form, name: normalizedName });
      }
      setShowModal(false);
      fetchList(buildParams());
    } catch (err) {
      alert(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  /* ───────── 삭제(비활성화) ───────── */
  const handleDeactivate = async () => {
    if (!deactivating) return;
    setDeactivateLoading(true);
    try {
      await brandsService.adminDeactivate(deactivating.id);
      setDeactivating(null);
      fetchList(buildParams());
    } catch (err) {
      alert(getApiErrorMessage(err));
    } finally {
      setDeactivateLoading(false);
    }
  };

  const handleReactivate = async (item: AdminBrand) => {
    try {
      await brandsService.adminUpdate(item.id, { is_active: true });
      fetchList(buildParams());
    } catch (err) {
      alert(getApiErrorMessage(err));
    }
  };

  const columns: Column<AdminBrand>[] = [
    { key: "id", title: "ID", width: "60px" },
    {
      key: "name",
      title: "브랜드명",
      render: (v, row) => (
        <div className="brand-list__name">
          <span>{v}</span>
          {row.is_default && <Badge variant="info">기본</Badge>}
        </div>
      ),
    },
    {
      key: "description",
      title: "설명",
      render: (v) => <span className="brand-list__desc">{v || "-"}</span>,
    },
    {
      key: "partner_url",
      title: "파트너 URL",
      render: (v: string) => (
        <a className="brand-list__link" href={v} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
          {v}
        </a>
      ),
    },
    {
      key: "is_active",
      title: "상태",
      width: "80px",
      render: (v) => (v ? <Badge variant="active">활성</Badge> : <Badge variant="draft">비활성</Badge>),
    },
    {
      key: "actions",
      title: "관리",
      width: "160px",
      render: (_v, row) => (
        <div className="brand-list__actions" onClick={(e) => e.stopPropagation()}>
          <Button variant="outline" size="sm" onClick={() => openEditModal(row)}>
            수정
          </Button>
          {row.is_active ? (
            <Button variant="danger" size="sm" disabled={row.is_default} title={row.is_default ? "기본 브랜드는 비활성화할 수 없습니다." : undefined} onClick={() => setDeactivating(row)}>
              비활성화
            </Button>
          ) : (
            <Button variant="secondary" size="sm" onClick={() => handleReactivate(row)}>
              재활성화
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="admin-page">
      <ContentHeader
        title="브랜드 관리"
        description="강사·파트너가 소속되는 브랜드를 관리합니다. 삭제 대신 비활성화로 처리됩니다."
        actions={[
          <Button key="add" variant="primary" onClick={openCreateModal}>
            + 브랜드 추가
          </Button>,
        ]}
      />

      <div className="admin-page__card">
        <div className="admin-page__filter brand-list__filter">
          <Input placeholder="브랜드명 검색" value={search} onChange={(e) => setSearch(e.target.value)} prefix="🔍" />
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} options={BRAND_STATUS_FILTER_OPTIONS} />
        </div>

        {error && <div className="admin-page__error">{error}</div>}
        {loading ? <div className="admin-page__loading">불러오는 중...</div> : <Table columns={columns} data={items} rowKey="id" onRowClick={(row) => navigate(`/brand-list/${row.id}`)} />}
      </div>

      <Modal
        open={showModal}
        title={editing ? "브랜드 수정" : "브랜드 추가"}
        onClose={() => setShowModal(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              취소
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={saving || nameInvalid}>
              {saving ? "저장 중..." : editing ? "수정" : "추가"}
            </Button>
          </>
        }
      >
        <div className="brand-form">
          <Input label="브랜드명 *" placeholder="예: 더 딥스" value={form.name} maxLength={BRAND_NAME_MAX_LENGTH} disabled={editing?.is_default} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
          {editing?.is_default && <p className="brand-form__hint">기본 브랜드는 이름 변경과 비활성화를 할 수 없습니다.</p>}

          <div className="brand-form__field">
            <label className="brand-form__label" htmlFor="brand-description">
              설명
            </label>
            <textarea id="brand-description" className="brand-form__textarea" rows={4} placeholder="브랜드 설명을 입력해주세요." maxLength={BRAND_DESCRIPTION_MAX_LENGTH} value={form.description ?? ""} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
            <span className="brand-form__count">
              {(form.description ?? "").length} / {BRAND_DESCRIPTION_MAX_LENGTH}
            </span>
          </div>

          <Select label="상태" value={form.is_active ? "true" : "false"} disabled={editing?.is_default} onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.value === "true" }))} options={BRAND_STATUS_OPTIONS} />
        </div>
      </Modal>

      <Modal
        open={!!deactivating}
        title="브랜드 비활성화"
        width="420px"
        onClose={() => setDeactivating(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeactivating(null)}>
              취소
            </Button>
            <Button variant="danger" onClick={handleDeactivate} disabled={deactivateLoading}>
              {deactivateLoading ? "처리 중..." : "비활성화"}
            </Button>
          </>
        }
      >
        <p className="brand-list__confirm">
          <strong>{deactivating?.name}</strong> 브랜드를 비활성화하시겠습니까?
          <br />
          기존 소속 강사·파트너 연결은 유지되며, 언제든 재활성화할 수 있습니다.
        </p>
      </Modal>
    </div>
  );
};

export default BrandList;
